import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, Mail, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = ({ settings }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use settings from CMS or defaults
  const phone = settings?.phone || '(410) 123-4567';
  const email = settings?.email || 'info@perrydtires.com';
  const addressLine1 = settings?.address_line1 || '51 Main Street, Suite B';
  const addressLine2 = settings?.address_line2 || 'Reisterstown, MD 21136';
  const hoursWeekday = settings?.hours_weekday || 'Mon-Sat: 8AM - 6PM';
  const hoursWeekend = settings?.hours_weekend || 'Sunday: Closed';

  const contactInfo = [
    { icon: MapPin, title: 'Address', content: addressLine1, subtitle: addressLine2 },
    { icon: Phone, title: 'Phone', content: phone, subtitle: 'Call us anytime' },
    { icon: Clock, title: 'Hours', content: hoursWeekday, subtitle: hoursWeekend },
    { icon: Mail, title: 'Email', content: email, subtitle: 'Get in touch' },
  ];

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format phone for tel: link
  const phoneLink = phone.replace(/\D/g, '');

  return (
    <section id="contact" className="py-16 lg:py-24 bg-[#0A0A0A] relative" data-testid="contact-section">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF2E2E]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 lg:mb-16"
        >
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl text-white mb-4">Contact Us</h2>
          <p className="font-body text-white/60 text-base lg:text-lg max-w-2xl mx-auto">
            Ready to get your tires serviced? Reach out to us today!
          </p>
          <div className="w-16 lg:w-24 h-1 bg-[#FF2E2E] mx-auto mt-4 lg:mt-6" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="glass p-6 lg:p-8" data-testid="contact-form">
              <h3 className="font-heading text-xl lg:text-2xl text-white mb-6">Send us a Message</h3>
              <div className="space-y-4 lg:space-y-5">
                <div>
                  <label className="font-body text-white/60 text-sm block mb-2">Name *</label>
                  <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your name"
                    className="bg-[#121212] border-white/10 text-white placeholder:text-white/30 rounded-none h-12" />
                </div>
                <div>
                  <label className="font-body text-white/60 text-sm block mb-2">Email *</label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com"
                    className="bg-[#121212] border-white/10 text-white placeholder:text-white/30 rounded-none h-12" />
                </div>
                <div>
                  <label className="font-body text-white/60 text-sm block mb-2">Phone (Optional)</label>
                  <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder={phone}
                    className="bg-[#121212] border-white/10 text-white placeholder:text-white/30 rounded-none h-12" />
                </div>
                <div>
                  <label className="font-body text-white/60 text-sm block mb-2">Message *</label>
                  <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="How can we help?"
                    rows={4} className="bg-[#121212] border-white/10 text-white placeholder:text-white/30 rounded-none resize-none" />
                </div>
                <Button type="submit" disabled={isSubmitting}
                  className="w-full bg-[#FF2E2E] hover:bg-[#D61E1E] text-white font-heading tracking-wider py-5 lg:py-6 rounded-none">
                  {isSubmitting ? 'Sending...' : <><Send className="w-5 h-5 mr-2" /> Send Message</>}
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-4 lg:space-y-6"
          >
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              {contactInfo.map((info, index) => (
                <div key={info.title} className="glass border-glow p-4 lg:p-6">
                  <div className="flex items-center gap-3 mb-2 lg:mb-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 border border-[#FF2E2E]/30 bg-[#FF2E2E]/10 flex items-center justify-center">
                      <info.icon className="w-4 h-4 lg:w-5 lg:h-5 text-[#FF2E2E]" />
                    </div>
                    <span className="font-heading text-sm lg:text-base text-white">{info.title}</span>
                  </div>
                  <p className="font-body text-white/90 text-sm lg:text-base">{info.content}</p>
                  <p className="font-body text-white/50 text-xs lg:text-sm">{info.subtitle}</p>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="glass overflow-hidden h-[200px] lg:h-[250px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3077.8!2d-76.8294!3d39.4697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c81be1c94b7c21%3A0x42b87ba8be4a8da0!2s51%20Main%20St%2C%20Reisterstown%2C%20MD%2021136!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%" height="100%" style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                allowFullScreen="" loading="lazy" title="Location" />
            </div>

            {/* Quick Call */}
            <a href={`tel:+1${phoneLink}`}
              className="glass border border-[#FF2E2E]/30 p-4 lg:p-6 flex items-center justify-between hover:border-[#FF2E2E] transition-all group">
              <div className="flex items-center gap-3 lg:gap-4">
                <div className="w-12 h-12 lg:w-14 lg:h-14 bg-[#FF2E2E] flex items-center justify-center">
                  <Phone className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                </div>
                <div>
                  <p className="font-heading text-lg lg:text-xl text-white">Call Now</p>
                  <p className="font-body text-white/60 text-sm">Quick service</p>
                </div>
              </div>
              <span className="font-heading text-xl lg:text-2xl text-[#FF2E2E] group-hover:text-white transition-colors hidden sm:block">
                {phone}
              </span>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
